import styled from '@emotion/styled';
import React, { useCallback, useState } from 'react';
import { OpportunityMode, ProjectStatus, TheaterType, UserRole } from '../../../enums';
import { SetDealModal, SetOpportunityModal, VerifyOwnerModal } from '../../modals';
import LinkedIcon from './LinkedIcon';
import UnlinkedIcon from './UnlinkedIcon';

enum LinkOwnershipButtonModalState {
  GreenWithExistingOpportunity,
  GreenWithExistingDeal,
  YellowWithIncompleteExistingOpportunity,
  YellowWithEmptyDeal,
  YellowWithVerifyOwner,
  Hidden,
}

export interface LinkOwnershipButtonProps {
  projectId: number;
  defaultTheater: TheaterType;
  userRole: UserRole | string;
  ownershipVerified: boolean;
  ownerId: number;
  projectStatus: ProjectStatus | string;
  ownerCompanySfdcAccountId?: string;
  ownerCompanySfdcAccountName?: string;
  projectSfdcAccountId?: string;
  projectSfdcAccountName?: string;
  opportunityId?: number;
  opportunityMode?: OpportunityMode;
  dealId?: string;
  onSuccess: () => void;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

export const LinkOwnershipButton = (props: LinkOwnershipButtonProps) => {
  const [showLinkOpportunityButtonModal, setShowLinkOpportunityButtonModal] =
    useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  const toggleModal = useCallback(
    (value: boolean) => () => {
      setShowLinkOpportunityButtonModal(value);
      if (!value && success) props.onSuccess();
    },
    [props, success]
  );

  const handleSuccess = useCallback(() => setSuccess(true), []);

  const isCurrentOwnerLinkedToSalesforce = (ownerCompanySfdcAccountId?: string): boolean => {
    return (
      ownerCompanySfdcAccountId !== undefined &&
      ownerCompanySfdcAccountId !== '' &&
      ownerCompanySfdcAccountId !== 'NA' &&
      ownerCompanySfdcAccountId !== null
    );
  };

  const getModalState = () => {
    if (props.ownerId === 1 && !props.ownershipVerified)
      return LinkOwnershipButtonModalState.YellowWithVerifyOwner;
    else if (props.ownerId !== 1)
      if (isCurrentOwnerLinkedToSalesforce(props.ownerCompanySfdcAccountId))
        return props.opportunityId
          ? LinkOwnershipButtonModalState.GreenWithExistingOpportunity
          : LinkOwnershipButtonModalState.YellowWithIncompleteExistingOpportunity;
      else
        return props.dealId
          ? LinkOwnershipButtonModalState.GreenWithExistingDeal
          : LinkOwnershipButtonModalState.YellowWithEmptyDeal;
    else return LinkOwnershipButtonModalState.Hidden;
  };

  const renderLinkOwnershipButton = (
    linkOwnershipButtonModalState: LinkOwnershipButtonModalState
  ) => {
    switch (linkOwnershipButtonModalState) {
      case LinkOwnershipButtonModalState.GreenWithExistingDeal:
      case LinkOwnershipButtonModalState.GreenWithExistingOpportunity:
        return <LinkedIcon />;
      case LinkOwnershipButtonModalState.YellowWithEmptyDeal:
      case LinkOwnershipButtonModalState.YellowWithIncompleteExistingOpportunity:
      case LinkOwnershipButtonModalState.YellowWithVerifyOwner:
        return <UnlinkedIcon />;
      default:
        return <></>;
    }
  };

  const renderLinkOwnershipButtonModal = (
    linkOwnershipButtonModalState: LinkOwnershipButtonModalState
  ) => {
    switch (linkOwnershipButtonModalState) {
      case LinkOwnershipButtonModalState.GreenWithExistingDeal:
        return (
          <SetDealModal
            projectId={props.projectId}
            onClose={toggleModal(false)}
            onSuccess={handleSuccess}
            defaultDealId={props.dealId}
          />
        );
      case LinkOwnershipButtonModalState.YellowWithEmptyDeal:
        return (
          <SetDealModal
            projectId={props.projectId}
            onClose={toggleModal(false)}
            onSuccess={handleSuccess}
          />
        );
      case LinkOwnershipButtonModalState.GreenWithExistingOpportunity:
      case LinkOwnershipButtonModalState.YellowWithIncompleteExistingOpportunity:
        return (
          <SetOpportunityModal
            projectId={props.projectId}
            defaultTheater={props.defaultTheater}
            defaultSalesforceInfo={{
              opportunityId: props.opportunityId,
              salesforceAccountId:
                props.projectSfdcAccountId && props.projectSfdcAccountName
                  ? props.projectSfdcAccountId
                  : props.ownerCompanySfdcAccountId,
              salesforceAccountName:
                props.projectSfdcAccountId && props.projectSfdcAccountName
                  ? props.projectSfdcAccountName
                  : props.ownerCompanySfdcAccountName,
              opportunityMode: OpportunityMode[props.opportunityMode ?? OpportunityMode.None],
            }}
            onClose={toggleModal(false)}
            onSuccess={handleSuccess}
          />
        );
      case LinkOwnershipButtonModalState.YellowWithVerifyOwner:
        return (
          <VerifyOwnerModal
            isReassign={false}
            projectId={props.projectId}
            defaultTheater={props.defaultTheater}
            onSuccess={handleSuccess}
            onClose={toggleModal(false)}
          />
        );
    }
  };

  const isValidProject =
    (props.userRole === UserRole.DellAdminUser || props.userRole === UserRole.DellUser) &&
    (props.projectStatus === ProjectStatus.Completed ||
      props.projectStatus === ProjectStatus.CompletedAndEmailSent);
  if (isValidProject) {
    const linkOwnershipButtonModalState = getModalState();

    return (
      <>
        <Container onClick={toggleModal(true)}>
          {renderLinkOwnershipButton(linkOwnershipButtonModalState)}
        </Container>
        {showLinkOpportunityButtonModal ? (
          renderLinkOwnershipButtonModal(linkOwnershipButtonModalState)
        ) : (
          <></>
        )}
      </>
    );
  }
  return <></>;
};
